import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, Users, Building2, Package, TrendingUp, TrendingDown, LogIn, Plus, Edit, Trash2, UserPlus, UserCheck, UserMinus, Shield, Settings, Eye, Play, Pause, RefreshCw } from 'lucide-react';
import HubGeographicDistribution from '@/components/analytics/HubGeographicDistribution';
import CONFIG from '@/lib/config';

interface DashboardStats {
  totalHubs: number;
  totalProjects: number;
  totalUsers: number;
  totalAccUsers: number;
  activeSubscriptions: number;
}

interface HubStats {
  id: string;
  name: string;
  projectCount: number;
  packageCount: number;
  activityCount: number;
  accMemberCount: number;
  hubAppUserCount: number;
  country: string;
  dataCentre: string;
  views: number;
}

interface RecentActivity {
  id: string;
  admin_user_id: string;
  admin_email: string;
  admin_name: string;
  activity_type: string;
  activity_category: string;
  description: string;
  target_entity_type?: string;
  target_entity_id?: string;
  target_entity_name?: string;
  changes_made?: any;
  previous_state?: any;
  new_state?: any;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  created_at: string;
  hours_ago?: string;
  days_ago?: string;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [hubStats, setHubStats] = useState<HubStats[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [activityFilter, setActivityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Auto refresh logic - every 20 seconds
  useEffect(() => {
    // Clear existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    
    // Set new interval - refresh every 20 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 20000);
    
    setRefreshInterval(interval);
    
    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);



  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard stats using session authentication
      const statsResponse = await fetch(`${CONFIG.API_BASE_URL}/api/admin/dashboard/stats`, {
        credentials: 'include', // Important for session cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setStats(data.stats);
      } else {
        console.error('Dashboard stats API failed:', statsResponse.status, statsResponse.statusText);
        if (statsResponse.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
          return;
        }
      }

      // Fetch hub stats for geographic distribution
      const hubStatsResponse = await fetch(`${CONFIG.API_BASE_URL}/api/admin/hub-stats`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (hubStatsResponse.ok) {
        const hubData = await hubStatsResponse.json();
        console.log('Hub stats API response:', hubData);
        console.log('Setting hubStats to:', hubData.hubStats || []);
        setHubStats(hubData.hubStats || []);
      } else {
        console.error('Hub stats API failed:', hubStatsResponse.status, hubStatsResponse.statusText);
        if (hubStatsResponse.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
          return;
        }
      }

      // Fetch recent admin activities (latest 30)
      const activitiesResponse = await fetch(`${CONFIG.API_BASE_URL}/api/admin/activities?limit=30`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (activitiesResponse.ok) {
        const activitiesData = await activitiesResponse.json();
        console.log('Admin activities API response:', activitiesData);
        setRecentActivities(activitiesData.activities || []);
      } else {
        console.error('Admin activities API failed:', activitiesResponse.status, activitiesResponse.statusText);
        if (activitiesResponse.status === 401) {
          localStorage.removeItem('adminToken');
          window.location.href = '/admin/login';
          return;
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityIcon = (activityType: string, category: string) => {
    switch (activityType) {
      case 'login':
        return <LogIn className="w-4 h-4 text-blue-500" />;
      case 'hub_create':
        return <Plus className="w-4 h-4 text-green-500" />;
      case 'hub_update':
        return <Edit className="w-4 h-4 text-orange-500" />;
      case 'hub_delete':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'user_create':
        return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'user_update':
        return <UserCheck className="w-4 h-4 text-orange-500" />;
      case 'user_delete':
        return <UserMinus className="w-4 h-4 text-red-500" />;
      case 'user_permission_change':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'settings_update':
        return <Settings className="w-4 h-4 text-gray-500" />;
      case 'dashboard_view':
        return <Eye className="w-4 h-4 text-purple-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'authentication':
        return 'bg-blue-100 text-blue-800';
      case 'hub_management':
        return 'bg-green-100 text-green-800';
      case 'user_management':
        return 'bg-orange-100 text-orange-800';
      case 'system_settings':
        return 'bg-gray-100 text-gray-800';
      case 'system_access':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderChangesDetails = (activity: RecentActivity) => {
    if (!activity.changes_made || typeof activity.changes_made !== 'object' || Object.keys(activity.changes_made).length === 0) {
      return null;
    }

    return (
      <div className="mt-2 p-2 bg-blue-50 rounded border-l-4 border-blue-200">
        <div className="text-xs font-medium text-blue-800 mb-1">Changes Made:</div>
        <div className="space-y-1">
          {Object.entries(activity.changes_made).map(([field, change], index) => {
            if (typeof change === 'object' && change !== null && 'old' in change && 'new' in change) {
              const changeObj = change as { old: any; new: any };
              const formatValue = (value: any) => {
                if (typeof value === 'boolean') {
                  return value ? 'Active' : 'Expired';
                }
                return String(value);
              };
              
              return (
                <div key={index} className="text-xs text-blue-700">
                  <span className="font-medium">{field}:</span>
                  <span className="text-red-600"> {formatValue(changeObj.old)}</span>
                  <span className="mx-1">→</span>
                  <span className="text-green-600">{formatValue(changeObj.new)}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  const getActivityTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'login': 'Login',
      'hub_create': 'Create Hub',
      'hub_update': 'Update Hub',
      'hub_delete': 'Delete Hub',
      'user_create': 'Create User',
      'user_update': 'Update User',
      'user_delete': 'Delete User',
      'user_permission_change': 'Change Permissions',
      'settings_update': 'Update Settings',
      'dashboard_view': 'View Dashboard'
    };
    return labels[type] || type;
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      'authentication': 'Authentication',
      'hub_management': 'Hub Management',
      'user_management': 'User Management',
      'system_settings': 'System Settings',
      'system_access': 'System Access'
    };
    return labels[category] || category;
  };

  // Filter activities based on selected filters
  const filteredActivities = recentActivities.filter(activity => {
    const matchesActivityType = !activityFilter || activity.activity_type === activityFilter;
    const matchesCategory = !categoryFilter || activity.activity_category === categoryFilter;
    return matchesActivityType && matchesCategory;
  });

  // Convert hub stats to world map format
  const getWorldMapData = () => {
    console.log('getWorldMapData called, hubStats length:', hubStats.length);
    console.log('getWorldMapData called, hubStats:', hubStats);
    const countryMap = new Map<string, { country: string; flag: string; views: number }>();
    
    // Country to flag emoji mapping
    const countryFlags: { [key: string]: string } = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Germany': '🇩🇪',
      'Australia': '🇦🇺',
      'Canada': '🇨🇦',
      'France': '🇫🇷',
      'Japan': '🇯🇵',
      'Singapore': '🇸🇬',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Switzerland': '🇨🇭',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Belgium': '🇧🇪',
      'Austria': '🇦🇹',
      'Finland': '🇫🇮',
      'Ireland': '🇮🇪',
      'New Zealand': '🇳🇿',
      'South Korea': '🇰🇷',
      'India': '🇮🇳',
      'China': '🇨🇳',
      'Brazil': '🇧🇷',
      'Mexico': '🇲🇽',
      'Argentina': '🇦🇷',
      'Chile': '🇨🇱',
      'South Africa': '🇿🇦',
      'United Arab Emirates': '🇦🇪',
      'Saudi Arabia': '🇸🇦',
      'Israel': '🇮🇱',
      'Turkey': '🇹🇷',
      'Poland': '🇵🇱',
      'Czech Republic': '🇨🇿',
      'Hungary': '🇭🇺',
      'Romania': '🇷🇴',
      'Bulgaria': '🇧🇬',
      'Croatia': '🇭🇷',
      'Slovenia': '🇸🇮',
      'Slovakia': '🇸🇰',
      'Estonia': '🇪🇪',
      'Latvia': '🇱🇻',
      'Lithuania': '🇱🇹',
      'Luxembourg': '🇱🇺',
      'Malta': '🇲🇹',
      'Cyprus': '🇨🇾',
      'Greece': '🇬🇷',
      'Portugal': '🇵🇹',
      'Iceland': '🇮🇸',
      'Liechtenstein': '🇱🇮',
      'Monaco': '🇲🇨',
      'San Marino': '🇸🇲',
      'Vatican City': '🇻🇦',
      'Andorra': '🇦🇩',
      'Malaysia': '🇲🇾',
      'Thailand': '🇹🇭',
      'Vietnam': '🇻🇳',
      'Philippines': '🇵🇭',
      'Indonesia': '🇮🇩',
      'Taiwan': '🇹🇼',
      'Hong Kong': '🇭🇰',
      'Macau': '🇲🇴',
      'Russia': '🇷🇺',
      'Ukraine': '🇺🇦',
      'Belarus': '🇧🇾',
      'Moldova': '🇲🇩',
      'Georgia': '🇬🇪',
      'Armenia': '🇦🇲',
      'Azerbaijan': '🇦🇿',
      'Kazakhstan': '🇰🇿',
      'Uzbekistan': '🇺🇿',
      'Kyrgyzstan': '🇰🇬',
      'Tajikistan': '🇹🇯',
      'Turkmenistan': '🇹🇲',
      'Mongolia': '🇲🇳',
      'North Korea': '🇰🇵',
      'Myanmar': '🇲🇲',
      'Laos': '🇱🇦',
      'Cambodia': '🇰🇭',
      'Bangladesh': '🇧🇩',
      'Sri Lanka': '🇱🇰',
      'Nepal': '🇳🇵',
      'Bhutan': '🇧🇹',
      'Maldives': '🇲🇻',
      'Pakistan': '🇵🇰',
      'Afghanistan': '🇦🇫',
      'Iran': '🇮🇷',
      'Iraq': '🇮🇶',
      'Syria': '🇸🇾',
      'Lebanon': '🇱🇧',
      'Jordan': '🇯🇴',
      'Kuwait': '🇰🇼',
      'Qatar': '🇶🇦',
      'Bahrain': '🇧🇭',
      'Oman': '🇴🇲',
      'Yemen': '🇾🇪',
      'Egypt': '🇪🇬',
      'Libya': '🇱🇾',
      'Tunisia': '🇹🇳',
      'Algeria': '🇩🇿',
      'Morocco': '🇲🇦',
      'Western Sahara': '🇪🇭',
      'Mauritania': '🇲🇷',
      'Mali': '🇲🇱',
      'Chad': '🇹🇩',
      'Sudan': '🇸🇩',
      'South Sudan': '🇸🇸',
      'Ethiopia': '🇪🇹',
      'Eritrea': '🇪🇷',
      'Djibouti': '🇩🇯',
      'Somalia': '🇸🇴',
      'Kenya': '🇰🇪',
      'Uganda': '🇺🇬',
      'Tanzania': '🇹🇿',
      'Rwanda': '🇷🇼',
      'Burundi': '🇧🇮',
      'Democratic Republic of the Congo': '🇨🇩',
      'Republic of the Congo': '🇨🇬',
      'Central African Republic': '🇨🇫',
      'Cameroon': '🇨🇲',
      'Nigeria': '🇳🇬',
      'Burkina Faso': '🇧🇫',
      'Ghana': '🇬🇭',
      'Ivory Coast': '🇨🇮',
      'Liberia': '🇱🇷',
      'Sierra Leone': '🇸🇱',
      'Guinea': '🇬🇳',
      'Guinea-Bissau': '🇬🇼',
      'Senegal': '🇸🇳',
      'Gambia': '🇬🇲',
      'Cape Verde': '🇨🇻',
      'Equatorial Guinea': '🇬🇶',
      'Gabon': '🇬🇦',
      'São Tomé and Príncipe': '🇸🇹',
      'Angola': '🇦🇴',
      'Zambia': '🇿🇲',
      'Zimbabwe': '🇿🇼',
      'Botswana': '🇧🇼',
      'Namibia': '🇳🇦',
      'Lesotho': '🇱🇸',
      'Eswatini': '🇸🇿',
      'Madagascar': '🇲🇬',
      'Comoros': '🇰🇲',
      'Mauritius': '🇲🇺',
      'Seychelles': '🇸🇨',
      'Reunion': '🇷🇪',
      'Mayotte': '🇾🇹',
      'Greenland': '🇬🇱',
      'Guatemala': '🇬🇹',
      'Belize': '🇧🇿',
      'El Salvador': '🇸🇻',
      'Honduras': '🇭🇳',
      'Nicaragua': '🇳🇮',
      'Costa Rica': '🇨🇷',
      'Panama': '🇵🇦',
      'Colombia': '🇨🇴',
      'Venezuela': '🇻🇪',
      'Guyana': '🇬🇾',
      'Suriname': '🇸🇷',
      'French Guiana': '🇬🇫',
      'Ecuador': '🇪🇨',
      'Peru': '🇵🇪',
      'Bolivia': '🇧🇴',
      'Paraguay': '🇵🇾',
      'Uruguay': '🇺🇾',
      'Falkland Islands': '🇫🇰',
      'South Georgia': '🇬🇸',
      'Antarctica': '🇦🇶'
    };

    // Aggregate hub data by country - use the same logic as HubGeographicDistribution
    hubStats.forEach(hub => {
      const country = hub.country || 'Unknown';
      const flag = countryFlags[country] || '🌍';
      const currentData = countryMap.get(country);
      
      // Use hubAppUserCount for App Users (same as HubGeographicDistribution)
      const value = hub.hubAppUserCount || 0;
      
      if (currentData) {
        currentData.views += value;
      } else {
        countryMap.set(country, {
          country,
          flag,
          views: value
        });
      }
    });

    // Convert to array and sort by views
    const result = Array.from(countryMap.values())
      .sort((a, b) => b.views - a.views)
      .slice(0, 20); // Top 20 countries
    
    console.log('getWorldMapData result:', result);
    return result;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Overview of your application's performance and activity
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hubs</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalHubs || 0}</div>
            <p className="text-xs text-muted-foreground">
              Active hubs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeSubscriptions || 0}/{stats?.totalHubs || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.totalHubs ? Math.round((stats.activeSubscriptions / stats.totalHubs) * 100) : 0}% active subscribed hubs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalProjects || 0}</div>
            <p className="text-xs text-muted-foreground">
              Active projects
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">App Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground">
              Application users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ACC Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalAccUsers || 0}</div>
            <p className="text-xs text-muted-foreground">
              Autodesk ACC users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Hub Geographic Distribution - Full Width */}
      <div className="mb-8">
        <HubGeographicDistribution 
          locationsData={getWorldMapData()} 
          hubStats={hubStats}
        />
      </div>

      {/* Recent Activity - Below Map */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Activity</CardTitle>
            <div className="flex gap-2">
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                className="px-3 py-1 text-sm border rounded-md"
              >
                <option value="">All Activities</option>
                <option value="login">Login</option>
                <option value="hub_update">Hub Updates</option>
                <option value="hub_create">Hub Creation</option>
                <option value="user_update">User Updates</option>
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1 text-sm border rounded-md"
              >
                <option value="">All Categories</option>
                <option value="authentication">Authentication</option>
                <option value="hub_management">Hub Management</option>
                <option value="user_management">User Management</option>
                <option value="system_settings">System Settings</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {!filteredActivities || filteredActivities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No recent activity
              </div>
            ) : (
              filteredActivities.map((activity) => (
                <div key={activity.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.activity_type, activity.activity_category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-sm">
                          {activity.admin_name}
                        </span>
                        <Badge variant="outline" className={`text-xs ${getCategoryColor(activity.activity_category)}`}>
                          {getCategoryLabel(activity.activity_category)}
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700">
                          {getActivityTypeLabel(activity.activity_type)}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">
                        {activity.description}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 space-x-2 mb-2">
                        <span>{activity.admin_email}</span>
                        <span>•</span>
                        <span>{formatDate(activity.created_at)}</span>
                        {activity.target_entity_name && (
                          <>
                            <span>•</span>
                            <span className="font-medium">{activity.target_entity_name}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Render detailed changes if available */}
                      {renderChangesDetails(activity)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>


    </div>
  );
}
